import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { debounce } from "lodash";
import Logger from "../../services/Logger";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UtilService from "../../services/UtilService";
import {
    PAY_PLAN_APP_LABELS,
    PAY_PLAN_APPS_FOR_ADMIN,
    payPlanChipColor,
    resolvePayPlan,
} from "../../services/PayPlanUtil";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import AccountService from "./AccountService";
import {
    ASSIGNABLE_USER_ROLES,
    formatUserRoles,
    resolveUserRoles,
    rolesEqual,
    withBaseUserRole,
} from "./userRoles";
import AllSubs from "./details/AllSubs";
import AssistantCreditsAdmin from "./details/AssistantCreditsAdmin";
import MobileCreditsAdmin from "./details/MobileCreditsAdmin";
import NotificationTestAdmin from "./details/NotificationTestAdmin";
import PuzzleCoinsAdmin from "./details/PuzzleCoinsAdmin";
import CurrentSubs from "./details/CurrentSubs";
import ForceEndDialog from "./details/ForceEndDialog";
import JigsawColorBasketAdmin from "./details/JigsawColorBasketAdmin";
import JigsawPackGrantsAdmin from "./details/JigsawPackGrantsAdmin";
import PlayRecordAdmin from "./details/PlayRecordAdmin";
import StripeStatusCheckDialog from "./details/StripeStatusCheckDialog";
import WordWheelAdmin from "./details/WordWheelAdmin";
import WebStripePurchases from "./details/WebStripePurchases";
import AdminTutorBedrockLog from "../tutor/AdminTutorBedrockLog";

/** Opens the Stripe Dashboard subscription page (test vs live). */
function stripeSubscriptionDashboardUrl(stripeSubscriptionId, livemode) {
    if (!stripeSubscriptionId) {
        return null;
    }
    const origin =
        livemode === true
            ? "https://dashboard.stripe.com"
            : "https://dashboard.stripe.com/test";
    return `${origin}/subscriptions/${encodeURIComponent(stripeSubscriptionId)}`;
}

const USER_DETAILS_TAB_STORAGE_KEY = "admin_account_user_details_tab";
const USER_DETAILS_TAB_COUNT = 8;

function readStoredUserDetailsTab() {
    try {
        const stored = localStorage.getItem(USER_DETAILS_TAB_STORAGE_KEY);
        let parsed = stored != null ? parseInt(stored, 10) : 0;
        if (!Number.isFinite(parsed) || parsed < 0) {
            return 0;
        }
        const migrated = localStorage.getItem(`${USER_DETAILS_TAB_STORAGE_KEY}_tutor`);
        if (!migrated && parsed >= 4 && parsed < 7) {
            parsed += 1;
            localStorage.setItem(USER_DETAILS_TAB_STORAGE_KEY, String(parsed));
        }
        if (!migrated) {
            localStorage.setItem(`${USER_DETAILS_TAB_STORAGE_KEY}_tutor`, "1");
        }
        if (parsed >= USER_DETAILS_TAB_COUNT) {
            return 0;
        }
        return parsed;
    } catch {
        return 0;
    }
}

export default function UserDetails() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forceEnding, setForceEnding] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [forceEndDialogOpen, setForceEndDialogOpen] = useState(false);
    const [roleChangeDialogOpen, setRoleChangeDialogOpen] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState(["USER"]);
    const [pendingRoles, setPendingRoles] = useState(null);
    const [updatingRole, setUpdatingRole] = useState(false);
    const [updatingTestAccount, setUpdatingTestAccount] = useState(false);
    const [resettingTestAccount, setResettingTestAccount] = useState(false);
    const [resetTestDialogOpen, setResetTestDialogOpen] = useState(false);
    const [updatingStaff, setUpdatingStaff] = useState(false);
    const [updatingDeveloper, setUpdatingDeveloper] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [memoDraft, setMemoDraft] = useState("");
    const [stripeChecking, setStripeChecking] = useState(false);
    const [stripeCanceling, setStripeCanceling] = useState(false);
    const [cancelStripeDialogOpen, setCancelStripeDialogOpen] = useState(false);
    const [stripeStatusCheckDialogOpen, setStripeStatusCheckDialogOpen] = useState(false);
    const [stripeStatusCheckResult, setStripeStatusCheckResult] = useState(null);
    const [activeTab, setActiveTab] = useState(readStoredUserDetailsTab);
    const memoSaveContextRef = useRef({ cloudUserId: null, userId: null });
    memoSaveContextRef.current = { cloudUserId: userDetails?.id ?? null, userId: userId ?? null };

    const debouncedSaveMemo = useMemo(
        () =>
            debounce(async (memoText) => {
                const { cloudUserId, userId: uid } = memoSaveContextRef.current;
                if (!cloudUserId || !uid) return;
                try {
                    await AccountService.updateUserMemo(cloudUserId, memoText);
                    const userData = await AccountService.getUserDetails(uid);
                    if (userData) {
                        setUserDetails(userData);
                    }
                    setSnackbarMessage("Memo is saved");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                } catch (err) {
                    const errorMessage = err.message || "Failed to save memo";
                    setSnackbarMessage(errorMessage);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                    Logger.error("Error saving memo:", err);
                }
            }, 600),
        []
    );

    useEffect(() => {
        return () => debouncedSaveMemo.cancel();
    }, [debouncedSaveMemo]);

    useEffect(() => {
        debouncedSaveMemo.cancel();
    }, [userId, debouncedSaveMemo]);

    const handleTabChange = (_e, value) => {
        setActiveTab(value);
        try {
            localStorage.setItem(USER_DETAILS_TAB_STORAGE_KEY, String(value));
        } catch (err) {
            Logger.warn("Failed to save user details tab to localStorage:", err);
        }
    };

    const handleBackClick = () => {
        navigate("/admin/account");
    };

    const handleOpenForceEndDialog = () => {
        setForceEndDialogOpen(true);
    };

    const handleCloseForceEndDialog = () => {
        setForceEndDialogOpen(false);
    };

    const handleForceEnd = async () => {
        if (!userDetails?.currSub?.id) {
            setError("No subscription ID found");
            setForceEndDialogOpen(false);
            return;
        }

        try {
            setForceEnding(true);
            setError(null);
            setForceEndDialogOpen(false);
            
            const response = await AccountService.forceEndSubscription(userDetails.currSub.id);
            
            // Logger.log("Force end subscription response:", response);
            
            // Reload user data to reflect the change
            const userData = await AccountService.getUserDetails(userId);
            
            if (userData) {
                setUserDetails(userData);
            }
            
            
            setSnackbarMessage("Subscription has been force ended successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            const errorMessage = err.message || "Failed to force end subscription";
            setError(errorMessage);
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error force ending subscription:", err);
        } finally {
            setForceEnding(false);
        }
    };

    const handleCheckStripe = async () => {
        const sub = userDetails?.currSub;
        if (!sub?.id) {
            return;
        }
        if (userDetails?.stripeLivemode === true) {
            const customerId = userDetails?.stripeCustomerId;
            if (!customerId || String(customerId).trim() === "") {
                setSnackbarMessage("No Stripe customer ID on record for this user.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }
            try {
                setStripeChecking(true);
                setError(null);
                const result = await AccountService.stripeStatusCheck(customerId);
                Logger.log("Stripe status-check result:", result);
                setStripeStatusCheckResult(result);
                setStripeStatusCheckDialogOpen(true);
                const userData = await AccountService.getUserDetails(userId);
                if (userData) {
                    setUserDetails(userData);
                }
            } catch (err) {
                const errorMessage = err.message || "Failed to check subscription on Stripe";
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                Logger.error("Stripe check error:", err);
            } finally {
                setStripeChecking(false);
            }
            return;
        }
        if (!sub.stripeSubscriptionId) {
            setSnackbarMessage("No Stripe subscription ID on record for this user.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }
        const url = stripeSubscriptionDashboardUrl(sub.stripeSubscriptionId, false);
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const handleOpenCancelStripeDialog = () => {
        const sub = userDetails?.currSub;
        if (!sub?.id) {
            return;
        }
        if (userDetails?.stripeLivemode === true) {
            const customerId = userDetails?.stripeCustomerId;
            if (!customerId || String(customerId).trim() === "") {
                setSnackbarMessage("No Stripe customer ID on record for this user.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }
        } else if (!sub.stripeSubscriptionId) {
            setSnackbarMessage("No Stripe subscription ID on record for this user.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }
        setCancelStripeDialogOpen(true);
    };

    const handleRequestCancelFromStatusDialog = () => {
        setStripeStatusCheckDialogOpen(false);
        setStripeStatusCheckResult(null);
        setCancelStripeDialogOpen(true);
    };

    const handleCloseStripeStatusCheckDialog = () => {
        setStripeStatusCheckDialogOpen(false);
        setStripeStatusCheckResult(null);
    };

    const handleConfirmCancelStripe = async () => {
        setCancelStripeDialogOpen(false);
        const sub = userDetails?.currSub;
        if (userDetails?.stripeLivemode === true) {
            const customerId = userDetails?.stripeCustomerId;
            if (!customerId || String(customerId).trim() === "") {
                return;
            }
            try {
                setStripeCanceling(true);
                setError(null);
                const result = await AccountService.stripeCancelForCustomer(customerId);
                const userData = await AccountService.getUserDetails(userId);
                if (userData) {
                    setUserDetails(userData);
                }
                setStripeStatusCheckDialogOpen(false);
                setStripeStatusCheckResult(null);
                const ids = result?.canceledStripeSubscriptionIds;
                const msg =
                    Array.isArray(ids) && ids.length > 0
                        ? `Canceled on Stripe: ${ids.join(", ")}`
                        : "Stripe cancel completed.";
                setSnackbarMessage(msg);
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            } catch (err) {
                const errorMessage = err.message || "Failed to cancel subscription on Stripe";
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                Logger.error("Cancel on Stripe error:", err);
            } finally {
                setStripeCanceling(false);
            }
            return;
        }
        if (sub?.stripeSubscriptionId) {
            const url = stripeSubscriptionDashboardUrl(sub.stripeSubscriptionId, false);
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
            setSnackbarMessage("Opened Stripe (test). Cancel the subscription in the Dashboard if needed.");
            setSnackbarSeverity("info");
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!userId) {
                    setError("User ID is missing");
                    setLoading(false);
                    return;
                }

                Logger.log("Fetching data for userId:", userId);

                const userData = await AccountService.getUserDetails(userId);

                Logger.log("Fetched userData:", userData);

                if (userData) {
                    setUserDetails(userData);
                    setSelectedRoles(resolveUserRoles(userData));
                } else {
                    setError("User details not found");
                }
            } catch (err) {
                const errorMessage = err.message || "Failed to load subscription details";
                setError(errorMessage);
                Logger.error("Error in fetchData:", err);
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchData();
        } else {
            setError("User ID is required");
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (userDetails) {
            setMemoDraft(typeof userDetails.memo === "string" ? userDetails.memo : "");
        }
    }, [userDetails]);

    const handleRoleChange = (event) => {
        const nextRoles = withBaseUserRole(event.target.value);
        const currentRoles = resolveUserRoles(userDetails);
        if (!rolesEqual(nextRoles, currentRoles)) {
            setPendingRoles(nextRoles);
            setRoleChangeDialogOpen(true);
        }
    };

    const handleTestAccountChange = async (event) => {
        const checked = event.target.checked;
        if (!userDetails?.id) return;
        try {
            setUpdatingTestAccount(true);
            await AccountService.updateTestAccount(userDetails.id, checked);
            const userData = await AccountService.getUserDetails(userId);
            if (userData) {
                setUserDetails(userData);
            }
            setSnackbarMessage(`Test account set to ${checked ? "on" : "off"}.`);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            const errorMessage = err.message || "Failed to update test account";
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error updating test account:", err);
        } finally {
            setUpdatingTestAccount(false);
        }
    };

    const handleConfirmResetTestAccount = async () => {
        if (!userDetails?.id) return;
        try {
            setResettingTestAccount(true);
            await AccountService.resetTestAccount(userDetails.id);
            const userData = await AccountService.getUserDetails(userId);
            if (userData) {
                setUserDetails(userData);
            }
            setSnackbarMessage("Play records, pay plans, and coins were reset. The account is still enabled.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            const errorMessage = err.message || "Failed to reset test account";
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error resetting test account:", err);
        } finally {
            setResettingTestAccount(false);
        }
    };

    const handleStaffChange = async (event) => {
        const checked = event.target.checked;
        if (!userDetails?.id) return;
        try {
            setUpdatingStaff(true);
            await AccountService.updateStaff(userDetails.id, checked);
            const userData = await AccountService.getUserDetails(userId);
            if (userData) {
                setUserDetails(userData);
            }
            setSnackbarMessage(`Staff set to ${checked ? "on" : "off"}.`);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            const errorMessage = err.message || "Failed to update staff flag";
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error updating staff flag:", err);
        } finally {
            setUpdatingStaff(false);
        }
    };

    const handleDeveloperChange = async (event) => {
        const checked = event.target.checked;
        if (!userDetails?.id) return;
        try {
            setUpdatingDeveloper(true);
            await AccountService.updateDeveloper(userDetails.id, checked);
            const userData = await AccountService.getUserDetails(userId);
            if (userData) {
                setUserDetails(userData);
            }
            setSnackbarMessage(`Developer set to ${checked ? "on" : "off"}.`);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (err) {
            const errorMessage = err.message || "Failed to update developer flag";
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error updating developer flag:", err);
        } finally {
            setUpdatingDeveloper(false);
        }
    };

    const handleConfirmRemove = async (id) => {
        if (!id) {
            setRemoveDialogOpen(false);
            return;
        }
        try {
            setRemoving(true);
            await AccountService.removeUser(id);
            navigate("/admin/account", { replace: true });
        } catch (err) {
            const errorMessage = err.message || "Failed to remove user";
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error removing user:", err);
        } finally {
            setRemoving(false);
        }
    };

    const handleConfirmRoleChange = async () => {
        if (!pendingRoles?.length || !userDetails?.id) {
            setRoleChangeDialogOpen(false);
            return;
        }

        try {
            setUpdatingRole(true);
            setRoleChangeDialogOpen(false);
            
            await AccountService.updateUserRole(userDetails.id, pendingRoles);
            
            // Reload user data to reflect the change
            const userData = await AccountService.getUserDetails(userId);
            
            if (userData) {
                setUserDetails(userData);
                setSelectedRoles(resolveUserRoles(userData));
            }
            
            setSnackbarMessage(`User roles have been updated to ${formatUserRoles(pendingRoles)}.`);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setPendingRoles(null);
        } catch (err) {
            const errorMessage = err.message || "Failed to update user role";
            setError(errorMessage);
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            Logger.error("Error updating user role:", err);
        } finally {
            setUpdatingRole(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    /** Stripe production customer — block destructive admin removal from this UI. */
    const isStripeLiveProduction = userDetails?.stripeLivemode === true;

    return (
        <Box sx={{ p: 2 }}>
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackClick}
                    variant="text"
                    color="primary"
                >
                    Back to Account List
                </Button>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="User details sections"
                >
                    <Tab label="User" id="user-tab-0" aria-controls="user-tabpanel-0" />
                    <Tab label="Play Record" id="user-tab-1" aria-controls="user-tabpanel-1" />
                    <Tab label="Word Wheel" id="user-tab-2" aria-controls="user-tabpanel-2" />
                    <Tab label="Jigsaw" id="user-tab-3" aria-controls="user-tabpanel-3" />
                    <Tab label="WEB(Stripe)" id="user-tab-4" aria-controls="user-tabpanel-4" />
                    <Tab label="Subscriptions & credits" id="user-tab-5" aria-controls="user-tabpanel-5" />
                    <Tab label="Notification" id="user-tab-6" aria-controls="user-tabpanel-6" />
                    <Tab label="Settings" id="user-tab-7" aria-controls="user-tabpanel-7" />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <Box role="tabpanel" id="user-tabpanel-0" aria-labelledby="user-tab-0">
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                User Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {userDetails?.firstName && userDetails?.lastName
                                            ? `${userDetails.firstName} ${userDetails.lastName}`
                                            : userDetails?.name || "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nickname
                                    </Typography>
                                    <Typography variant="body1">
                                        {typeof userDetails?.details?.nickname === "string"
                                        && userDetails.details.nickname.trim()
                                            ? userDetails.details.nickname.trim()
                                            : "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Email
                                    </Typography>
                                    <Typography variant="body1">{userDetails?.email || "N/A"}</Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Email Verified
                                    </Typography>
                                    <Chip
                                        label={userDetails?.emailVerified ? "Yes" : "No"}
                                        color={userDetails?.emailVerified ? "success" : "error"}
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        User Status
                                    </Typography>
                                    <Chip
                                        label={userDetails?.userStatus || userDetails?.userStatue || "N/A"}
                                        color={userDetails?.userStatus === "CONFIRMED" || userDetails?.userStatue === "CONFIRMED" ? "success" : "default"}
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Enabled
                                    </Typography>
                                    <Chip
                                        label={userDetails?.enabled ? "Yes" : "No"}
                                        color={userDetails?.enabled ? "success" : "error"}
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        User Roles
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="user-role-select-label">Roles</InputLabel>
                                        <Select
                                            labelId="user-role-select-label"
                                            id="user-role-select"
                                            multiple
                                            value={selectedRoles}
                                            onChange={handleRoleChange}
                                            disabled={updatingRole || loading}
                                            input={<OutlinedInput label="Roles" />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {selected.map((role) => (
                                                        <Chip key={role} label={role} size="small" />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            {ASSIGNABLE_USER_ROLES.map((role) => (
                                                <MenuItem key={role} value={role} disabled={role === "USER"}>
                                                    <Checkbox checked={selectedRoles.includes(role)} />
                                                    {role}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Pay plans
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                        {PAY_PLAN_APPS_FOR_ADMIN.map((appCode) => {
                                            const plan = resolvePayPlan(userDetails, appCode);
                                            const label = PAY_PLAN_APP_LABELS[appCode] ?? appCode;
                                            return (
                                                <Chip
                                                    key={appCode}
                                                    label={`${label}: ${plan}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color={payPlanChipColor(plan)}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Signup source
                                    </Typography>
                                    <Typography variant="body1">
                                        {userDetails?.signupSource != null && userDetails.signupSource !== ""
                                            ? userDetails.signupSource
                                            : "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Cloud user ID
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                                    >
                                        {userDetails?.id || "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Stripe Customer ID
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                                    >
                                        {userDetails?.stripeCustomerId != null && userDetails.stripeCustomerId !== ""
                                            ? userDetails.stripeCustomerId
                                            : "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Stripe live mode
                                    </Typography>
                                    {userDetails?.stripeLivemode == null ? (
                                        <Typography variant="body1">N/A</Typography>
                                    ) : (
                                        <Chip
                                            label={userDetails.stripeLivemode ? "Live (production)" : "Test"}
                                            color={userDetails.stripeLivemode ? "success" : "default"}
                                            size="small"
                                        />
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Created At
                                    </Typography>
                                    <Typography variant="body1">
                                        {UtilService.formatDate(userDetails?.createdAt) || "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Updated At
                                    </Typography>
                                    <Typography variant="body1">
                                        {userDetails?.updatedAt && userDetails.updatedAt !== 0
                                            ? UtilService.formatDate(userDetails.updatedAt) || "N/A"
                                            : "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }} container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Memo
                                        </Typography>
                                        <TextField
                                            multiline
                                            rows={3}
                                            fullWidth
                                            size="small"
                                            value={memoDraft}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setMemoDraft(v);
                                                debouncedSaveMemo(v);
                                            }}
                                            placeholder="Internal notes (admins only)"
                                        />
                                    </Grid>
                                    <Grid
                                        size={{ xs: 12, sm: 6 }}
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            alignItems: "start",
                                            justifyContent: { xs: "flex-start", sm: "flex-start" },
                                            pt: { xs: 0, sm: 3 },
                                        }}
                                    >
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mr: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(userDetails?.testAccount)}
                                                        onChange={handleTestAccountChange}
                                                        disabled={updatingTestAccount || resettingTestAccount}
                                                        color="primary"
                                                    />
                                                }
                                                label="Test Account"
                                            />
                                            {Boolean(userDetails?.testAccount) && (
                                                <Button
                                                    variant="outlined"
                                                    color="warning"
                                                    size="small"
                                                    onClick={() => setResetTestDialogOpen(true)}
                                                    disabled={resettingTestAccount}
                                                    sx={{ ml: 4, minHeight: 44 }}
                                                >
                                                    {resettingTestAccount ? "Resetting…" : "Reset"}
                                                </Button>
                                            )}
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={Boolean(userDetails?.staff)}
                                                    onChange={handleStaffChange}
                                                    disabled={updatingStaff}
                                                    color="primary"
                                                />
                                            }
                                            label="Staff"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={Boolean(userDetails?.isDeveloper ?? userDetails?.developer)}
                                                    onChange={handleDeveloperChange}
                                                    disabled={updatingDeveloper}
                                                    color="primary"
                                                />
                                            }
                                            label="Developer"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Social
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Sign in with Apple and Google identifiers (federated accounts).
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Apple Sign In sub
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                                    >
                                        {userDetails?.appleSub != null && userDetails.appleSub !== ""
                                            ? userDetails.appleSub
                                            : "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Apple sub linked at
                                    </Typography>
                                    <Typography variant="body1">
                                        {userDetails?.appleSubDate != null && userDetails.appleSubDate !== 0
                                            ? UtilService.formatDate(userDetails.appleSubDate) || "—"
                                            : "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Google sub
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                                    >
                                        {userDetails?.googleSub != null && userDetails.googleSub !== ""
                                            ? userDetails.googleSub
                                            : "—"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Google sub linked at
                                    </Typography>
                                    <Typography variant="body1">
                                        {userDetails?.googleSubDate != null && userDetails.googleSubDate !== 0
                                            ? UtilService.formatDate(userDetails.googleSubDate) || "—"
                                            : "—"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {userDetails?.details && Object.keys(userDetails.details).length > 0 && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom>
                                    User Options
                                </Typography>
                                <Grid container spacing={2}>
                                    {userDetails.details.avatarUrl && (
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Avatar
                                            </Typography>
                                            <Avatar
                                                src={userDetails.details.avatarUrl}
                                                alt="User Avatar"
                                                sx={{ width: 80, height: 80 }}
                                            />
                                        </Grid>
                                    )}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Push notifications (by app)
                                        </Typography>
                                        {userDetails.details?.notificationsByApp
                                        && typeof userDetails.details.notificationsByApp === "object"
                                        && Object.keys(userDetails.details.notificationsByApp).length > 0 ? (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                {Object.entries(userDetails.details.notificationsByApp).map(
                                                    ([app, on]) => (
                                                        <Chip
                                                            key={app}
                                                            label={`${app}: ${on ? "On" : "Off"}`}
                                                            color={on ? "success" : "default"}
                                                            size="small"
                                                            variant={on ? "filled" : "outlined"}
                                                        />
                                                    )
                                                )}
                                            </Box>
                                        ) : (
                                            <Chip
                                                label="Default (on)"
                                                color="success"
                                                size="small"
                                            />
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Show Hints
                                        </Typography>
                                        <Chip
                                            label={userDetails.details.showHints ? "Yes" : "No"}
                                            color={userDetails.details.showHints ? "success" : "default"}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Manual Validation
                                        </Typography>
                                        <Chip
                                            label={userDetails.details.manualValidation ? "Yes" : "No"}
                                            color={userDetails.details.manualValidation ? "success" : "default"}
                                            size="small"
                                        />
                                    </Grid>
                                    {userDetails.details.language && (
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Language
                                            </Typography>
                                            <Typography variant="body1">{userDetails.details.language}</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    <AdminTutorBedrockLog
                        cloudUserId={userDetails?.id ?? userId}
                        showUser={false}
                    />

                </Box>
            )}

            {activeTab === 1 && (
                <Box role="tabpanel" id="user-tabpanel-1" aria-labelledby="user-tab-1">
                    {/* Play Statistics moved here from User tab */}
                    <PlayRecordAdmin userId={userId} cloudUserId={userDetails?.id ?? userId} />
                </Box>
            )}

            {activeTab === 2 && (
                <Box role="tabpanel" id="user-tabpanel-2" aria-labelledby="user-tab-2">
                    <WordWheelAdmin cloudUserId={userDetails?.id ?? userId} />
                </Box>
            )}

            {activeTab === 3 && (
                <Box role="tabpanel" id="user-tabpanel-3" aria-labelledby="user-tab-3">
                    <JigsawPackGrantsAdmin cloudUserId={userDetails?.id ?? userId} />
                    <JigsawColorBasketAdmin cloudUserId={userDetails?.id ?? userId} />
                </Box>
            )}

            {activeTab === 4 && (
                <Box role="tabpanel" id="user-tabpanel-4" aria-labelledby="user-tab-4">
                    <WebStripePurchases
                        userId={userId}
                        userDetails={userDetails}
                        onGranted={async () => {
                            const userData = await AccountService.getUserDetails(userId);
                            if (userData) {
                                setUserDetails(userData);
                            }
                        }}
                    />
                </Box>
            )}

            {activeTab === 5 && (
                <Box role="tabpanel" id="user-tabpanel-5" aria-labelledby="user-tab-5">
                    <CurrentSubs
                        currSub={userDetails?.currSub}
                        stripeLivemode={userDetails?.stripeLivemode}
                        stripeCustomerId={userDetails?.stripeCustomerId}
                        onForceEndClick={handleOpenForceEndDialog}
                        forceEnding={forceEnding}
                        onCheckStripeClick={handleCheckStripe}
                        onCancelStripeClick={
                            userDetails?.stripeLivemode === true ? undefined : handleOpenCancelStripeDialog
                        }
                        stripeChecking={stripeChecking}
                        stripeCanceling={
                            userDetails?.stripeLivemode === true ? undefined : stripeCanceling
                        }
                    />

                    <AllSubs userId={userId} />

                    <AssistantCreditsAdmin userDetails={userDetails} userId={userId} />

                    <MobileCreditsAdmin cloudUserId={userDetails?.id ?? userId} />

                    <PuzzleCoinsAdmin
                        cloudUserId={userDetails?.id ?? userId}
                        puzzleCoins={userDetails?.puzzleCoins ?? null}
                        onPuzzleCoinsChange={(nextCoinsByApp) =>
                            setUserDetails((prev) => (prev ? { ...prev, puzzleCoins: nextCoinsByApp } : prev))
                        }
                    />
                </Box>
            )}

            {activeTab === 6 && (
                <Box role="tabpanel" id="user-tabpanel-6" aria-labelledby="user-tab-6">
                    <NotificationTestAdmin cloudUserId={userDetails?.id ?? userId} />
                </Box>
            )}

            {activeTab === 7 && (
                <Box role="tabpanel" id="user-tabpanel-7" aria-labelledby="user-tab-7">
                    <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="dangerous-zone-content"
                            id="dangerous-zone-header"
                        >
                            <Typography variant="h5" color="error" fontWeight={600} component="span">
                                Dangerous zone
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Permanently delete this user. This cannot be undone.
                            </Alert>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                This runs the same irreversible pipeline as self-service data deletion: Stripe subscription handling,
                                avatar and referrals, feedback, plays, checkout sessions, payment and subscription history,
                                subscriptions and Stripe customer mapping, database user row, and Cognito account removal.
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    disabled={removing || isStripeLiveProduction}
                                    onClick={() => setRemoveDialogOpen(true)}
                                >
                                    {removing ? "Removing…" : "Remove"}
                                </Button>
                                {isStripeLiveProduction && (
                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right", maxWidth: 420 }}>
                                        This user is on the production.
                                    </Typography>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            <StripeStatusCheckDialog
                open={stripeStatusCheckDialogOpen}
                onClose={handleCloseStripeStatusCheckDialog}
                result={stripeStatusCheckResult}
                stripeCanceling={stripeCanceling}
                onRequestCancelOnStripe={handleRequestCancelFromStatusDialog}
            />

            <ConfirmDialog
                open={cancelStripeDialogOpen}
                setOpen={setCancelStripeDialogOpen}
                title="Cancel on Stripe?"
                message={
                    userDetails?.stripeLivemode === true
                        ? [
                              "This will cancel active Stripe subscriptions for this customer via the server and update local data.",
                              "The user’s access may change according to your billing rules.",
                          ]
                        : [
                              "For test-mode customers this opens the subscription in the Stripe test Dashboard.",
                              "Complete cancellation there if you need to end the subscription.",
                          ]
                }
                targetId={userDetails?.id}
                callback={handleConfirmCancelStripe}
                warningAlert={
                    userDetails?.stripeLivemode === true
                        ? "Live Stripe — this affects a real paying customer."
                        : undefined
                }
            />

            {/* Force End Confirmation Dialog */}
            <ForceEndDialog
                open={forceEndDialogOpen}
                onClose={handleCloseForceEndDialog}
                onConfirm={handleForceEnd}
                forceEnding={forceEnding}
            />

            <ConfirmDialog
                open={resetTestDialogOpen}
                setOpen={setResetTestDialogOpen}
                title="Reset test account progress?"
                message={[
                    "This clears all play records, pay plans, coins, credits, and local subscription history.",
                    "The account stays enabled and can still log in. Identity, roles, and the Test Account flag are kept.",
                ]}
                targetName={userDetails?.email || userId}
                targetId={userDetails?.id}
                callback={handleConfirmResetTestAccount}
                warningAlert="An active Stripe subscription may restore a paid plan later if it is still billed."
            />

            {/* Role Change Confirmation Dialog */}
            <ConfirmDialog
                open={roleChangeDialogOpen}
                setOpen={setRoleChangeDialogOpen}
                title="Change User Roles?"
                message={`Would you like to change the user roles from ${formatUserRoles(resolveUserRoles(userDetails))} to ${formatUserRoles(pendingRoles)}?`}
                targetId={userDetails?.id}
                callback={handleConfirmRoleChange}
            />

            <ConfirmDialog
                open={removeDialogOpen}
                setOpen={setRemoveDialogOpen}
                title="Remove user permanently?"
                message={[
                    "This will permanently delete this user and all associated data.",
                    "You will not be able to recover the account, billing history, or progress.",
                ]}
                targetName={userDetails?.email || userId}
                targetId={userId}
                callback={handleConfirmRemove}
                warningAlert="This action is irreversible. Confirm only if you intend to fully remove this user."
            />

            {/* Custom Snackbar */}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
}

